/**
 * FHIR Task mapping helpers.
 *
 * Reads the canonical FHIR Task for the read-only inbox (ENG-3764 D29: no user edits a Task). All Medplum-specific
 * logic is concentrated here so the page / components stay presentation-only.
 */

import type { Coding, Task } from '@medplum/fhirtypes';
import {
  TASK_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import { getDueDate } from '@unify/fhir-model/resources/task';

// Historical ad-hoc coding may still appear on Tasks created before custom
// create was removed. Inbox create no longer writes this code.

export const AD_HOC_TASK_CODE = 'ad-hoc' as const;

// ─── Due date ──────────────────────────────────────────────────────────
// The project convention is restriction.period.end for the task due date.
// This is consistent with the fhir-model getter/setter and the mock data.

export function getTaskDueDate(task: Task): string | undefined {
  return getDueDate(task);
}

export function isTaskOverdue(task: Task): boolean {
  const dueDate = getTaskDueDate(task);
  if (!dueDate) return false;
  if (task.status === 'completed' || task.status === 'cancelled') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  return due < today;
}

// ─── Task type label ───────────────────────────────────────────────────

/**
 * The Task type coding: the TASK_TYPE_SYSTEM coding (system-managed Tasks put the category
 * coding first), falling back to the first coding for legacy Tasks without a canonical type.
 */
export function getTaskTypeCoding(task: Task): Coding | undefined {
  const codings = task.code?.coding;
  return codings?.find((c) => c.system === TASK_TYPE_SYSTEM) ?? codings?.[0];
}

export function getTaskTypeLabel(task: Task): string {
  const coding = getTaskTypeCoding(task);
  return coding?.display || coding?.code || 'Other';
}

export function getTaskTypeCode(task: Task): string {
  return getTaskTypeCoding(task)?.code ?? 'other';
}

/** True when the task was created manually (ad-hoc) rather than by a system rule. */
export function isAdHocTask(task: Task): boolean {
  return task.code?.coding?.some(
    (c) => c.system === TASK_TYPE_SYSTEM && c.code === AD_HOC_TASK_CODE,
  ) ?? false;
}
